<script lang="ts" setup>
// Без этого бесполезного импорта отвалится маска в инпуте телефона
import { vMaska } from 'maska'
import { computed, ref } from 'vue'
import { Field as VeeField, defineRule } from 'vee-validate'

/* Utils */
import { phoneRequired, requiredValidator } from '@/utils/rulesOfValidate'

/* Utils */
import { sanitizePhoneInput } from '@/utils/input'

import { useLoginStore } from '~/store/login/login'
import { useCityStore } from '~/store/city/city'
import { CurrentForm } from '~/store/login/login.types'
import { useUserStore } from '~/store/user/user'
import {
  BadRequestError,
  NotFoundError,
  Success,
  TooManyRequestError,
  UnauthorizedUserError,
} from '~/api/results'
import { useAppStore } from '~/store/app/app'

const { showSnackbar } = useAppStore()
const loginStore = useLoginStore()
const userStore = useUserStore()
const {
  cityInfo: { tel_input_placeholder },
  optionsPhone: phoneOptions,
} = useCityStore()

const props = defineProps({
  signInRedirectPath: {
    type: String,
    required: false,
    default: null,
  },
})

const { $metrics } = useNuxtApp()

const emit = defineEmits(['signInByNamedUser', 'signInByUnknowUser'])

defineRule('phone', (value: string) =>
  phoneRequired(value, form.value.phone.placeholder.length),
)
defineRule('requiredPhone', requiredValidator)

const form = ref({
  phone: {
    id: 'phone',
    label: 'Номер телефона *',
    placeholder: tel_input_placeholder,
    code: phoneOptions?.code,
    mask: phoneOptions?.mask,
    length: phoneOptions?.length,
    rules: `requiredPhone|phone:${phoneOptions.length}`,
  },
  password: {
    show: false,
    id: 'password',
    label: 'Пароль *',
    value: '',
    rules: 'requiredPhone',
  },
})
const buttonDisabled = computed(
  () =>
    form.value.phone.mask?.length !== phoneValue.value.length ||
    !form.value.password.value,
)

const signInInProgress = ref(false)
const phoneWithoutMask = computed(() =>
  loginStore.getPhoneWithoutMask(phoneOptions.code),
)
const phoneValue = computed({
  get(): string {
    return loginStore.phone
  },
  set(value: string): void {
    loginStore.setPhone(sanitizePhoneInput(value, phoneOptions.code))
  },
})

// Опции для маски номера телефона
const options = ref({
  mask: phoneOptions.mask,
  eager: true,
})

const signInHandler = async (setError: Function) => {
  signInInProgress.value = true

  const result = await userStore.signIn({
    phone: phoneWithoutMask.value,
    password: form.value.password.value,
  })

  if (result instanceof Success) {
    const userInfo = userStore.info

    $metrics?.eventSendGoal({ ym: 'other_auth' })

    setTimeout(() => {
      if (userInfo?.fullName) {
        if (props.signInRedirectPath) {
          window.location.replace(props.signInRedirectPath)
        }
        emit('signInByNamedUser')
      } else {
        loginStore.changeCurrentForm(CurrentForm.SET_NAME)

        emit('signInByUnknowUser')
      }
    }, 1000)
  } else if (
    result instanceof NotFoundError ||
    result instanceof UnauthorizedUserError
  ) {
    showSnackbar({
      message: 'Неверный телефон или пароль',
    })
  } else if (result instanceof BadRequestError) {
    if (result.data.phone) {
      setError(result.data.phone)
    }

    if (result.data.password) {
      setError(result.data.password)
    }
  } else if (result instanceof TooManyRequestError) {
    showSnackbar({
      message: result.message,
    })
  } else {
    showSnackbar({
      message: 'Что-то пошло не так, попробуйте позже',
    })
  }

  signInInProgress.value = false
}
const resetPasswordHandler = () => {
  loginStore.changeCurrentForm(CurrentForm.REQUEST_CODE)
}
</script>

<template>
  <v-form class="form" @submit.prevent>
    <h1 class="login-title">У меня есть пароль</h1>

    <label class="label" :for="form.phone.id">{{ form.phone.label }}</label>

    <vee-field
      v-slot="{ field, errorMessage }"
      v-model="phoneValue"
      :rules="form.phone.rules"
      name="phone"
    >
      <app-input
        :id="form.phone.id"
        v-bind="field"
        v-maska:[options]
        :prefix="`+${form.phone.code}`"
        :placeholder="form.phone.placeholder"
        class="input phone"
        solo
        flat
        autocomplete="off"
        :error-messages="errorMessage"
        type="tel"
      />
    </vee-field>

    <label class="label" :for="form.password.id">
      {{ form.password.label }}
    </label>

    <vee-field
      v-slot="{ field, errorMessage }"
      v-model="form.password.value"
      :rules="form.password.rules"
      name="password"
    >
      <app-input
        :id="form.password.id"
        v-bind="field"
        class="input password"
        solo
        flat
        autocomplete="off"
        :error-messages="errorMessage"
        :type="form.password.show ? 'text' : 'password'"
      >
        <template #append-inner>
          <nuxt-img
            :src="form.password.show ? './blind-eye.svg' : './visible-eye.svg'"
            width="20"
            height="16"
            alt="Показать/Скрыть пароль"
            @click="form.password.show = !form.password.show"
          />
        </template>
      </app-input>
    </vee-field>

    <div class="buttons">
      <app-button
        type="submit"
        :disabled="buttonDisabled"
        :loading="signInInProgress"
        class="button sign-in"
        block
        @click="signInHandler"
      >
        Войти
      </app-button>
      <app-button
        class="button reset-password"
        variant="text"
        @click="resetPasswordHandler"
      >
        Не помню пароль
      </app-button>
    </div>
  </v-form>
</template>

<style lang="scss" scoped>
.form {
  width: 100%;
  max-width: 320px;
  margin: auto;
}
.login-title {
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: 800;
  text-align: center;
}
.label {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
}

.input {
  margin-bottom: 8px;
}

.buttons {
  margin-bottom: 20px;
  .button {
    height: 50px;
    font-size: 14px;
    font-weight: 700;
    line-height: 19px;
    text-transform: none;
    letter-spacing: 0;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(255, 80, 0, 0.16);
    &:not(:last-child) {
      margin-bottom: 28px;
    }
  }

  .reset-password {
    display: block;
    height: 24px;
    min-height: 24px;
    margin: auto;
    color: $primary;
  }
}
</style>
